import React, { useEffect, useState } from "react";
import PermissionGate from "../../components/common/PermissionGate";
import { PERMISSIONS } from "../../constants/permissions";
import RoleList from "../../components/roles/RoleList";
import RolePermissionsPanel from "../../components/roles/RolePermissionsPanel";
import CreateRoleModal from "../../components/roles/CreateRoleModal";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import "./RoleManagementPage.css";
import api from "../../api/client";
import { PLM_PERMISSION_SECTIONS } from "../dms/permissionSections";

// Manufacturing (and its Customer) permissions belong to the Manufacturing
// app's own Role Management, so PLM leaves them out.
const PLM_PERMISSIONS = Object.entries(PERMISSIONS)
  .filter(([key]) => !key.startsWith("MANUFACTURING_") && !key.startsWith("CUSTOMER_"))
  .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});

export default function RoleManagementPage() {
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editable, setEditable] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadRoles = React.useCallback(async () => {
    try {
      setError("");
      setLoading(true);
      const resp = await api.get("/roles");
      setRoles(resp.data);
      if (!selectedId && resp.data.length > 0) {
        setSelectedId(resp.data[0].id);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, [api, selectedId]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const selectedRole = roles.find((r) => r.id === selectedId);

  const handleCreate = async (body) => {
    try {
      setError("");
      const resp = await api.post("/roles", body);
      await loadRoles();
      setSelectedId(resp.data.id);
      setEditable(true);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create role");
    }
  };

  const handleSave = async () => {
    if (!selectedRole) return;
    try {
      setError("");
      await api.patch(`/roles/${selectedRole.id}`, {
        name: selectedRole.name,
        description: selectedRole.description,
        permissions: selectedRole.permissions,
      });
      setEditable(false);
      await loadRoles();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save role");
    }
  };

  const handleDelete = () => {
    if (!selectedRole) return;
    setDeleteConfirm(true);
  };

  const doDelete = async () => {
    try {
      setError("");
      await api.delete(`/roles/${selectedRole.id}`);
      setSelectedId(null);
      await loadRoles();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete role");
    }
  };

  const handlePermissionsChange = (perms) => {
    setRoles((prev) =>
      prev.map((r) => (r.id === selectedId ? { ...r, permissions: perms } : r)),
    );
  };

  const handleNameChange = (name) => {
    setRoles((prev) =>
      prev.map((r) => (r.id === selectedId ? { ...r, name } : r)),
    );
  };

  const handleDescriptionChange = (desc) => {
    setRoles((prev) =>
      prev.map((r) => (r.id === selectedId ? { ...r, description: desc } : r)),
    );
  };

  const totalRoles = roles.length;
  const systemRoles = roles.filter((r) => r.isSystem).length;
  const customRoles = totalRoles - systemRoles;

  return (
    <PermissionGate permission={PERMISSIONS.ROLE_VIEW}>
      <div className="rm-page">
        <header className="rm-header">
          <div>
            <h1>Role Management</h1>
            <p>Create and maintain permission sets for tenant users.</p>
          </div>
          <button
            className="rm-btn-primary"
            onClick={() => {
              setShowCreateModal(true);
            }}
          >
            + Create New Role
          </button>
        </header>

        <section className="rm-stats">
          <div className="rm-stat-card">
            <span>Total Roles</span>
            <strong>{totalRoles}</strong>
          </div>
          <div className="rm-stat-card">
            <span>Custom Roles</span>
            <strong>{customRoles}</strong>
          </div>
          <div className="rm-stat-card">
            <span>System Roles</span>
            <strong>{systemRoles}</strong>
          </div>
        </section>

        {error && <div className="rm-error">{error}</div>}

        {showCreateModal && (
          <CreateRoleModal
            roles={roles}
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreate}
          />
        )}

        <div className="rm-layout">
          <div className="rm-left-panel">
            <div className="rm-panel-title">Roles</div>
            {loading ? (
              <div className="rm-loading">Loading roles...</div>
            ) : (
              <RoleList
                roles={roles}
                selectedId={selectedId}
                onSelect={(id) => {
                  setSelectedId(id);
                  setEditable(false);
                }}
              />
            )}
          </div>
          <div className="rm-right-panel">
            <div className="rm-panel-title">Permissions</div>
            <RolePermissionsPanel
              role={selectedRole || {}}
              editable={editable}
              onChange={handlePermissionsChange}
              onNameChange={handleNameChange}
              onDescriptionChange={handleDescriptionChange}
              onSave={handleSave}
              onCancel={() => {
                setEditable(false);
                loadRoles();
              }}
              onEdit={() => setEditable(true)}
              onDelete={handleDelete}
              permissionsMap={PLM_PERMISSIONS}
              sections={PLM_PERMISSION_SECTIONS}
            />
          </div>
        </div>
      </div>
      {deleteConfirm && selectedRole && (
        <ConfirmDialog
          title="Delete Role"
          message={`This will remove the role from ${selectedRole.userCount} user(s). They will have no role until reassigned. Continue?`}
          confirmLabel="Delete Role"
          confirmClass="btn-danger"
          onConfirm={() => { setDeleteConfirm(false); doDelete(); }}
          onClose={() => setDeleteConfirm(false)}
        />
      )}
    </PermissionGate>
  );
}
